import { useEffect, useMemo, useState } from 'react';
import { usePartnerUserForms } from 'js/contexts/PartnerUserFormsContext';
import { useAuth } from 'js/contexts/AuthContext';

// LIBS
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

// SERVICES
import phoneMaskArray from 'js/components/LoginForms/phoneMaskArray.json';
import cfac22 from 'js/library/utils/cfac22';

const useGetClubSegmentation = () => ({ clubSegmentations: {} });
import {
  colorText,
  isValidDate,
  trackEventMatomoVisit,
  toStoredUploadPath,
} from 'js/library/utils/helpers';
import { fetchUserData } from './services';
import { mapFormByMode } from 'js/library/utils/formVisibility';

// STYLES
import { Box } from '@mui/material';
import GoBack from 'js/containers/GoBack';

// APIS
import { editUserPartner } from 'js/library/utils/API/apiEditUserPartner';
import { uploadFiles } from 'js/library/utils/API/uploadFiles';
import { sendConfirmationEmail } from 'js/library/utils/API/validateEmail/sendConfirmationEmail';

// PACKAGE
import { EditUserPackage } from '@sysmobil/synoro-packages';

const clone = (value) => JSON.parse(JSON.stringify(value || {}));

const upsertListItem = (list = [], item, key = 'type') => {
  if (!item?.[key]) return list;

  const next = [...list];
  const index = next.findIndex((current) => current?.[key] === item[key]);

  if (index >= 0) {
    next[index] = item;
  } else {
    next.push(item);
  }

  return next;
};

const getOptInKey = (optIn = {}) => optIn.optInId || optIn.type;

const isImmutableIdentifierField = (field = {}) =>
  field.fieldName === 'cellPhone' || field.indexKey === true;

const applyEditableFieldValue = ({ target, source, field }) => {
  if (!target || !source || !field?.fieldName) return;
  if (isImmutableIdentifierField(field)) return;

  const { fieldName, type } = field;

  if (type === 'contactList') {
    const contact = (source.contactList || []).find((item) => item.type === fieldName);
    if (contact) {
      target.contactList = upsertListItem(target.contactList || [], contact);
    }
    return;
  }

  if (type === 'documentList') {
    const document = (source.documentList || []).find((item) => item.type === fieldName);
    if (document) {
      target.documentList = upsertListItem(target.documentList || [], document);
    }
    return;
  }

  if (type === 'optInList') {
    const fallbackIds = [field.optInId, fieldName].filter(Boolean);
    const optIn = (source.optInList || []).find((item) => fallbackIds.includes(getOptInKey(item)));
    if (optIn) {
      target.optInList = upsertListItem(target.optInList || [], optIn, 'optInId');
    }
    return;
  }

  if (type === 'addressList') {
    if (Array.isArray(source.addressList)) {
      target.addressList = source.addressList;
    }
    return;
  }

  if (Object.prototype.hasOwnProperty.call(source, fieldName)) {
    target[fieldName] = source[fieldName];
  }
};

const mergePreData = (preData = {}, userInfo = {}) => {
  if (Array.isArray(preData) || Array.isArray(userInfo)) {
    const items = [
      ...(Array.isArray(preData) ? preData : []),
      ...(Array.isArray(userInfo) ? userInfo : []),
    ];
    const keyed = new Map();
    items.forEach((item) => {
      const key = item?.type || item?.optInId;
      if (key) keyed.set(key, { ...(keyed.get(key) || {}), ...item });
    });
    return keyed.size ? Array.from(keyed.values()) : items;
  }

  if (preData && typeof preData === 'object' && userInfo && typeof userInfo === 'object') {
    const result = { ...preData };
    Object.keys(userInfo).forEach((key) => {
      result[key] = key in result ? mergePreData(result[key], userInfo[key]) : userInfo[key];
    });
    return result;
  }

  return userInfo ?? preData;
};

const buildPreservedEditPayload = ({ userInfo, preData, valuesForm, visibleForm }) => {
  const payload = mergePreData(preData, userInfo);
  const values = clone(valuesForm);

  (visibleForm?.formFields || []).forEach((field) => {
    applyEditableFieldValue({ target: payload, source: values, field });
  });

  Object.entries(visibleForm?.otherBlocks || {}).forEach(([blockKey, block]) => {
    const sourceBlock = values.otherBlocks?.[blockKey];
    if (!sourceBlock) return;

    payload.otherBlocks ??= {};
    payload.otherBlocks[blockKey] = {
      ...(payload.otherBlocks[blockKey] || {}),
    };

    (block.fields || []).forEach((field) => {
      applyEditableFieldValue({
        target: payload.otherBlocks[blockKey],
        source: sourceBlock,
        field,
      });
    });
  });

  return payload;
};

export default function EditUser() {
  const {
    form,
    partnerId,
    loading: partnerFormLoading,
    error: partnerFormError,
    reload: reloadPartnerForm,
  } = usePartnerUserForms();
  const { authState } = useAuth();
  const userPersistInfo = authState.currentUserInfo || {};
  const preData = userPersistInfo;
  const isGuest = false;
  const visibleForm = useMemo(() => (form ? mapFormByMode(form, 'edit') : null), [form]);
  const seuClubePersist = { partnerId, clubeId: partnerId, customizations: {} };

  const navigate = useNavigate();

  const [userInfo, setUserInfo] = useState({});
  const [valuesForm, setValuesForm] = useState({});

  const [tabValue, setTabValue] = useState(0);
  const [emailGetUser, setEmailGetUser] = useState([]);
  const [loadingForm, setLoadingForm] = useState(true);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [files, setFiles] = useState({});
  const [fileImage, setFileImage] = useState({}); //avatar
  const [fieldErrors, setFieldErrors] = useState({ formFields: {}, otherBlocks: {} });

  useEffect(() => {
    if (isGuest) {
      return navigate('/validarAcesso?url=/meusDados');
    }

    fetchUserData({
      form,
      partnerId,
      userPersistInfo,
      setUserInfo,
      setValuesForm,
      setEmailGetUser,
      setLoadingForm,
      setFiles,
      setFileImage,
    });
    // eslint-disable-next-line
  }, [form?.formFields, form?.otherBlocks, partnerId, userPersistInfo.uId]);

  const segment = useGetClubSegmentation();
  const segmentsIds = JSON.parse(sessionStorage.getItem('segmentsIds') || '[]');

  const selectedSegment = useMemo(() => {
    if (isGuest || !segmentsIds?.length) return null;

    const segId = segmentsIds[0]?.trim();
    if (segment.clubSegmentations && segment.clubSegmentations[segId]) {
      return segment.clubSegmentations[segId];
    }

    const values = Object.values(segment.clubSegmentations || {});
    return values.length > 0 ? values[0] : null;
  }, [isGuest, segmentsIds, segment.clubSegmentations]);

  useEffect(() => {
    trackEventMatomoVisit('MeusDados');
  }, []);

  const handleInputChange = (event, input) => {
    if (isImmutableIdentifierField(input)) return;

    let value = event.target.value;
    const name = event.target.name;
    const checked = event.target.checked;

    if (name === 'zipCode' && value.length > 5) {
      value = value.replace(/\D/g, '');
      value = value.replace(/(\d{5})(\d{1,3})/, '$1-$2');
    }

    if (input.inputType === 'date' && value) {
      const [year, month, day] = value.split('-');
      value = `${day}/${month}/${year}`;
    }

    const updatedForm = { ...valuesForm };

    switch (input.type) {
      case 'root':
      case 'others': {
        const sectionKey = input.sectionKey || 'formfields';

        if (sectionKey === 'formfields') {
          if (input.inputType === 'date') {
            updatedForm[name] = value;
          } else if (input.fieldType === 'checkbox') {
            updatedForm[name] = checked;
          } else {
            updatedForm[name] = value;
          }
        } else {
          const otherBlocks = { ...updatedForm.otherBlocks };
          const section = otherBlocks[sectionKey] || {};

          if (input.inputType === 'date') {
            section[name] = value;
          } else if (input.fieldType === 'checkbox') {
            section[name] = checked;
          } else {
            section[name] = value;
          }

          otherBlocks[sectionKey] = section;
          updatedForm.otherBlocks = otherBlocks;
        }

        break;
      }

      case 'addressList': {
        if (input.sectionKey === 'formfields') {
          const addressList = [...(updatedForm.addressList || [])];
          const idx = addressList.findIndex((a) => a.type === input.fieldName);
          const fallbackIdx =
            idx === -1 && addressList.length === 1 && !addressList[0].type ? 0 : idx;
          const updatedAddress = {
            ...(fallbackIdx >= 0 ? addressList[fallbackIdx] : {}),
            type: input.fieldName,
            [name]: value,
          };

          if (fallbackIdx >= 0) {
            addressList[fallbackIdx] = updatedAddress;
          } else {
            addressList.push(updatedAddress);
          }

          updatedForm.addressList = addressList;
        } else {
          const sectionKey = input.sectionKey;
          const otherBlocks = { ...updatedForm.otherBlocks };
          const section = otherBlocks[sectionKey] || {};
          const addressList = [...(section.addressList || [])];

          const idx = addressList.findIndex((a) => a.type === input.fieldName);
          const fallbackIdx =
            idx === -1 && addressList.length === 1 && !addressList[0].type ? 0 : idx;
          const updatedAddress = {
            ...(fallbackIdx >= 0 ? addressList[fallbackIdx] : {}),
            type: input.fieldName,
            [name]: value,
          };

          if (fallbackIdx >= 0) {
            addressList[fallbackIdx] = updatedAddress;
          } else {
            addressList.push(updatedAddress);
          }

          otherBlocks[sectionKey] = {
            ...section,
            addressList,
          };

          updatedForm.otherBlocks = otherBlocks;
        }
        break;
      }

      case 'contactList': {
        const sectionKey = input.sectionKey || 'formfields';
        const updatedForm = { ...valuesForm };

        // Tratamento específico para telefone
        if (input.fieldType === 'cellPhone') {
          const cleanedValue = value.replace(/\D/g, '');
          const sectionKey = input.sectionKey || 'formfields';
          const isRoot = sectionKey === 'formfields';

          // Busca contato atual para manter a DDI (mask.ddd)
          const currentContact = isRoot
            ? valuesForm.contactList?.find((item) => item.type === input.fieldName)
            : valuesForm.otherBlocks?.[sectionKey]?.contactList?.find(
              (item) => item.type === input.fieldName
            );

          const currentMask = currentContact?.mask || phoneMaskArray.find((m) => m.ddd === '+55');

          const newContact = {
            type: input.fieldName,
            phone: cleanedValue,
            value: `${currentMask.ddd}${cleanedValue}`,
            mask: currentMask,
          };

          if (isRoot) {
            const contactList = [...(valuesForm.contactList || [])];
            const index = contactList.findIndex((item) => item.type === input.fieldName);
            if (index >= 0) contactList[index] = newContact;
            else contactList.push(newContact);
            setValuesForm({ ...valuesForm, contactList });
          } else {
            const otherBlocks = { ...valuesForm.otherBlocks };
            const section = otherBlocks[sectionKey] || {};
            const contactList = [...(section.contactList || [])];
            const index = contactList.findIndex((item) => item.type === input.fieldName);
            if (index >= 0) contactList[index] = newContact;
            else contactList.push(newContact);
            section.contactList = contactList;
            otherBlocks[sectionKey] = section;
            setValuesForm({ ...valuesForm, otherBlocks });
          }

          setFieldErrors?.((prev = {}) => {
            const next = {
              formFields: { ...(prev.formFields || {}) },
              otherBlocks: { ...(prev.otherBlocks || {}) },
            };

            if (sectionKey === 'formfields') {
              delete next.formFields[input.fieldName];
            } else {
              next.otherBlocks[sectionKey] = { ...(next.otherBlocks[sectionKey] || {}) };
              delete next.otherBlocks[sectionKey][input.fieldName];
              if (Object.keys(next.otherBlocks[sectionKey]).length === 0) {
                delete next.otherBlocks[sectionKey];
              }
            }
            return next;
          });

          return;
        }

        if (input.fieldType === 'email') {
          const newContact = {
            type: input.fieldName,
            value: value,
          };

          if (sectionKey === 'formfields') {
            const contactList = [...(updatedForm.contactList || [])];
            const index = contactList.findIndex((item) => item.type === input.fieldName);

            if (index >= 0) {
              contactList[index] = newContact;
            } else {
              contactList.push(newContact);
            }

            updatedForm.contactList = contactList;
          } else {
            const otherBlocks = { ...(updatedForm.otherBlocks || {}) };
            const section = otherBlocks[sectionKey] || {};
            const contactList = [...(section.contactList || [])];
            const index = contactList.findIndex((item) => item.type === input.fieldName);

            if (index >= 0) {
              contactList[index] = newContact;
            } else {
              contactList.push(newContact);
            }

            section.contactList = contactList;
            otherBlocks[sectionKey] = section;
            updatedForm.otherBlocks = otherBlocks;
          }

          setFieldErrors?.((prev = {}) => {
            const next = {
              formFields: { ...(prev.formFields || {}) },
              otherBlocks: { ...(prev.otherBlocks || {}) },
            };

            if (sectionKey === 'formfields') {
              delete next.formFields[input.fieldName];
            } else {
              next.otherBlocks[sectionKey] = { ...(next.otherBlocks[sectionKey] || {}) };
              delete next.otherBlocks[sectionKey][input.fieldName];
              if (Object.keys(next.otherBlocks[sectionKey]).length === 0) {
                delete next.otherBlocks[sectionKey];
              }
            }
            return next;
          });

          setValuesForm(updatedForm);
          return;
        }

        break;
      }

      case 'documentList': {
        const sectionKey = input.sectionKey || 'formfields';

        const cleanedValue = value.replace(/\D/g, '');
        const newDoc = { type: input.fieldName, value: cleanedValue };

        if (sectionKey === 'formfields') {
          const documentList = [...(updatedForm.documentList || [])];
          const index = documentList.findIndex((item) => item.type === input.fieldName);

          if (index >= 0) documentList[index] = newDoc;
          else documentList.push(newDoc);

          updatedForm.documentList = documentList;
        } else {
          const otherBlocks = { ...updatedForm.otherBlocks };
          const section = otherBlocks[sectionKey] || {};
          const documentList = [...(section.documentList || [])];
          const index = documentList.findIndex((item) => item.type === input.fieldName);

          if (index >= 0) documentList[index] = newDoc;
          else documentList.push(newDoc);

          section.documentList = documentList;
          otherBlocks[sectionKey] = section;
          updatedForm.otherBlocks = otherBlocks;
        }

        break;
      }

      case 'optInList': {
        const sectionKey = input.sectionKey || 'formfields';

        const existingOptInList =
          sectionKey === 'formfields'
            ? valuesForm.optInList || []
            : valuesForm.otherBlocks?.[sectionKey]?.optInList || [];

        const existingOptIn = existingOptInList.find(
          (item) =>
            item.optInId === input.optInId ||
            item.optInId === `${input.fieldName}_${sectionKey}` ||
            item.optInId === input.fieldName
        );

        const optInId =
          existingOptIn?.optInId || input.optInId || `${input.fieldName}_${sectionKey}`;

        const newOpt = {
          ...(existingOptIn || {}),
          accept: checked,
          optInId: optInId,
          type: input.label,
          version: 1,
          ...(checked ? { dateAcceptance: Date.now() } : {}),
        };

        if (sectionKey === 'formfields') {
          const optInList = [...(updatedForm.optInList || [])];
          const index = optInList.findIndex(
            (item) =>
              item.optInId === optInId ||
              item.optInId === `${input.fieldName}_${sectionKey}` ||
              item.optInId === input.fieldName
          );

          if (index >= 0) {
            optInList[index] = newOpt;
          } else {
            optInList.push(newOpt);
          }

          updatedForm.optInList = optInList;
        } else {
          const otherBlocks = { ...updatedForm.otherBlocks };
          const section = otherBlocks[sectionKey] || {};
          const optInList = [...(section.optInList || [])];

          const index = optInList.findIndex(
            (item) =>
              item.optInId === optInId ||
              item.optInId === `${input.fieldName}_${sectionKey}` ||
              item.optInId === input.fieldName
          );

          if (index >= 0) {
            optInList[index] = newOpt;
          } else {
            optInList.push(newOpt);
          }

          section.optInList = optInList;
          otherBlocks[sectionKey] = section;
          updatedForm.otherBlocks = otherBlocks;
        }

        break;
      }

      default: {
        const sectionKey = input.sectionKey || 'formfields';

        if (sectionKey === 'formfields') {
          updatedForm[name] = value;
        } else {
          updatedForm.otherBlocks = {
            ...(updatedForm.otherBlocks || {}),
            [sectionKey]: {
              ...(updatedForm.otherBlocks?.[sectionKey] || {}),
              [name]: value,
            },
          };
        }

        break;
      }
    }

    // Limpa erro do campo ao alterar valor
    setFieldErrors?.((prev = {}) => {
      const next = {
        formFields: { ...(prev.formFields || {}) },
        otherBlocks: { ...(prev.otherBlocks || {}) },
      };

      const sectionKey = input.sectionKey || 'formfields';

      if (sectionKey === 'formfields') {
        if (next.formFields[input.fieldName]) {
          delete next.formFields[input.fieldName];
        }
      } else {
        next.otherBlocks[sectionKey] = { ...(next.otherBlocks[sectionKey] || {}) };
        if (next.otherBlocks[sectionKey][input.fieldName]) {
          delete next.otherBlocks[sectionKey][input.fieldName];
        }
        if (Object.keys(next.otherBlocks[sectionKey]).length === 0) {
          delete next.otherBlocks[sectionKey];
        }
      }

      return next;
    });

    setValuesForm(updatedForm);
  };

  const handleValidateEmail = async () => {
    try {
      const res = await sendConfirmationEmail(userPersistInfo.uId, partnerId, 'pt-br');

      if (res.response) {
        toast.success(
          'Enviamos um email de confirmação para você. Por favor, verifique sua caixa de entrada.'
        );
      }
    } catch (e) {
      console.error('Ocorreu um erro ao validar email: ', e.error);
      toast.error(
        'Você solicitou a confirmação do seu e-mail recentemente. Se não recebeu a mensagem, entre em contato com nosso suporte.'
      );
    }
  };

  const handleSubmit = async () => {
    setLoading(true);

    let stopIteration = false;
    const cellPhoneInvalid = false;

    const formatDateToTimestamp = (dateStr) => {
      const [day, month, year] = dateStr.split('/');
      return new Date(`${year}-${month}-${day}`).getTime();
    };

    const userInfoObj = buildPreservedEditPayload({
      userInfo,
      preData,
      valuesForm,
      visibleForm,
    });

    const visibleRootContactTypes = new Set(
      (visibleForm.formFields || [])
        .filter((field) => field.type === 'contactList')
        .map((field) => field.fieldName)
    );

    if (Array.isArray(userInfoObj.contactList)) {
      userInfoObj.contactList = userInfoObj.contactList.map((contact) => {
        if (!visibleRootContactTypes.has(contact.type)) {
          return contact;
        }

        if (contact.type?.toLowerCase() === 'email') {
          const originalEmail = (valuesForm.contactList || []).find(
            (c) => c.type?.toLowerCase() === 'email'
          );
          // Se o e-mail não mudou e estava verificado, mantém os campos extras
          if (originalEmail && originalEmail.verified && originalEmail.value === contact.value) {
            return {
              type: contact.type,
              value: contact.value,
              verified: originalEmail.verified,
              dateVerified: originalEmail.dateVerified,
            };
          }
        }
        // Para todos os outros casos, mantém só type e value
        return {
          type: contact.type,
          value: contact.value,
        };
      });
    }

    // seção fixa (formFields)
    visibleForm.formFields.forEach((field) => {
      if (stopIteration) return;

      if (field.type === 'root' || field.type === 'others') {
        if (field.fieldType === 'date') {
          const rawDate = userInfoObj[field.fieldName];
          if (!isValidDate(rawDate)) {
            setLoading(false);
            toast.warn('Por favor, insira uma data válida!');
            stopIteration = true;
            return;
          }
          userInfoObj[field.fieldName] = formatDateToTimestamp(rawDate);
        }
      }

      // Validação de celular
      if (field.type === 'contactList' && field.fieldType === 'cellPhone') {
        const rawCell = valuesForm.contactList?.find((c) => c.type === 'cellPhone');

        // O usuário ainda não digitou ou não tem telefone
        if (!rawCell?.value) {
          setLoading(false);
          return toast.warn('Número de celular inválido.');
        }

        // Extrai o DDI do próprio campo de celular (vindo de handleCountryChange)
        const mask = rawCell.mask || phoneMaskArray.find((m) => m.ddd === '+55');
        const cleanedPhone = rawCell.value.replace(/\D/g, ''); // Remove tudo que não é número

        // Remove DDI do número completo para comparar só o "número local"
        const phoneWithoutDDD = cleanedPhone.replace(mask.ddd.replace('+', ''), '');

        // Define o comprimento esperado (sem o DDI)
        const expectedLength = mask.mask.replace(/\D/g, '').length;

        // Valida o número
        if (phoneWithoutDDD.length < expectedLength) {
          setLoading(false);
          return toast.warn('Número de celular inválido.');
        }
      }

      // CPF
      if (field.type === 'documentList' && field.fieldName === 'cpf') {
        const doc = (valuesForm.documentList || []).find((d) => d.type === 'cpf');
        if (!doc?.value || doc.value.replace(/\D/g, '').length < 11) {
          setLoading(false);
          toast.warn('Número de CPF inválido.');
          stopIteration = true;
          return;
        }
      }

      // CNPJ
      if (field.type === 'documentList' && field.fieldName === 'cnpj') {
        const doc = (valuesForm.documentList || []).find((d) => d.type === 'cnpj');
        if (doc && doc.value?.replace(/\D/g, '').length < 14) {
          setLoading(false);
          toast.warn('Número de CNPJ inválido.');
          stopIteration = true;
          return;
        }
      }
    });

    // file
    for (const field of visibleForm.formFields) {
      if (stopIteration) break;

      if (field.type === 'root' || field.type === 'others') {
        if (field.fieldType === 'file') {
          try {
            const fieldName = field.fieldName;
            const fileArray = files?.formfields?.[fieldName];

            if (fileArray?.length > 0) {
              const fileData = fileArray[0];
              const mimeType = fileData.mimeType || fileData.file?.mimeType;
              const buffer = fileData.buffer || fileData.file?.buffer;
              const fileName = fileData.name || fileData.file?.name || fieldName;

              let result;

              if (mimeType) {
                result = await uploadFiles('filesSeuClube', {
                  mimeType,
                  fileName,
                  buffer,
                });
              }

              if (result) {
                userInfoObj[fieldName] = {
                  name: fileName,
                  url: toStoredUploadPath(result),
                };
              }
            }
          } catch (error) {
            console.error(`Erro ao processar arquivo ${field.fieldName}:`, error);
            userInfoObj[field.fieldName] = valuesForm[field.fieldName];
          }
        }

        // avatar
        if (field.fieldType === 'avatar') {
          try {
            const fieldName = field.fieldName;
            const avatarData = fileImage?.formfields?.[fieldName];

            if (avatarData === 'removed') {
              userInfoObj[fieldName] = '';
            } else if (avatarData) {
              const mimeType = avatarData.mimeType;
              const buffer = avatarData.buffer;

              let result;

              if (mimeType) {
                result = await uploadFiles('userAvatar', {
                  mimeType,
                  fileName: `${userInfo?.aliasName ?? 'user'}_${seuClubePersist.clubeId
                    }_${fieldName}`,
                  buffer,
                });
              }

              userInfoObj[fieldName] = result || valuesForm[fieldName];
            }
          } catch (error) {
            console.error(`Erro ao processar avatar ${field.fieldName}:`, error);
            userInfoObj[field.fieldName] = valuesForm[field.fieldName];
          }
        }
      }
    }

    // ——— Validação dinâmica de formFields obrigatórios ———
    if (visibleForm.formFields) {
      const missingFixed = [];

      visibleForm.formFields.forEach((field) => {
        if (!field.active || !field.required) return;
        if (field.visibilityType === 'none') return;

        const name = field.fieldName;

        // 1) contactList (celular, e-mail, etc.)
        if (field.type === 'contactList') {
          const list = valuesForm.contactList || [];
          const item = list.find((c) => c.type === name);
          if (!item?.value) {
            missingFixed.push(name);
          } else if (field.fieldType === 'email') {
            // validação básica de formato de e-mail
            const re = /^\S+@\S+\.\S+$/;
            if (!re.test(item.value)) missingFixed.push(name);
          }
          return;
        }

        // 2) documentList (CPF, CNPJ, RG, RGIA, etc.)
        if (field.type === 'documentList') {
          const list = valuesForm.documentList || [];
          const doc = list.find((d) => d.type === name);
          if (!doc?.value) {
            missingFixed.push(name);
          } else {
            const digits = doc.value.replace(/\D/g, '');
            if (field.fieldType === 'cpf' && digits.length < 11) {
              missingFixed.push(name);
            } else if (field.fieldType === 'cnpj' && digits.length < 14) {
              missingFixed.push(name);
            }
            // para 'rg', 'rgia' e outros tipos de documento, basta existir valor
          }
          return;
        }

        // 3) addressList: precisa haver item e CEP válido
        if (field.type === 'addressList') {
          const arr = valuesForm.addressList || [];
          if (!Array.isArray(arr) || arr.length === 0) {
            missingFixed.push(name);
          } else {
            const zip = arr[0].zipCode || '';
            if (zip.replace(/\D/g, '').length < 8) {
              missingFixed.push(name);
            }
          }
          return;
        }

        // 4) optInList: ao menos um item
        if (field.type === 'optInList') {
          const arr = valuesForm.optInList || [];
          const accepted = arr.some(
            (o) =>
              (o.optInId === field.optInId && o.accept) ||
              (o.optInId === field.fieldName && o.accept) ||
              (o.optInId === `${field.fieldName}_formfields` && o.accept)
          );
          if (!accepted) {
            missingFixed.push(name);
          }
          return;
        }

        // 5) checkbox
        if (field.fieldType === 'checkbox') {
          if (!valuesForm[name]) missingFixed.push(name);
          return;
        }

        // 6) arquivo/avatar
        if (['file', 'avatar'].includes(field.fieldType)) {
          const fileVal = userInfoObj[name] || valuesForm[name];
          const hasFile =
            typeof fileVal === 'string'
              ? fileVal.trim() !== ''
              : fileVal && typeof fileVal.url === 'string' && fileVal.url.trim() !== '';
          if (!hasFile) missingFixed.push(name);
          return;
        }

        // 7) date
        if (field.fieldType === 'date') {
          if (!isValidDate(valuesForm[name])) missingFixed.push(name);
          return;
        }

        // 8) select / selectMultiple / maritalStatus
        if (
          field.fieldType === 'select' ||
          field.inputType === 'select' ||
          field.inputType === 'selectMultiple' ||
          field.fieldType === 'maritalStatus' ||
          field.inputType === 'maritalStatus'
        ) {
          const val = valuesForm[name];

          if (field.inputType === 'selectMultiple') {
            // Mesma lógica do getInvalidFields para consistência
            if (Array.isArray(val)) {
              if (val.length === 0) missingFixed.push(name);
            } else if (typeof val === 'string') {
              const trimmed = val.trim();
              if (trimmed === '') {
                missingFixed.push(name);
              } else {
                try {
                  const parsed = JSON.parse(trimmed);
                  if (Array.isArray(parsed) && parsed.length === 0) {
                    missingFixed.push(name);
                  }
                } catch {
                  // Não é JSON, verifica se tem conteúdo após split por vírgula
                  const parts = trimmed
                    .split(',')
                    .map((s) => s.trim())
                    .filter(Boolean);
                  if (parts.length === 0) {
                    missingFixed.push(name);
                  }
                }
              }
            } else if (val && typeof val === 'object') {
              // Se for objeto, verifica se tem propriedades
              if (Object.keys(val).length === 0) {
                missingFixed.push(name);
              }
            } else {
              // valor undefined, null, etc
              missingFixed.push(name);
            }
            return;
          }

          // Para outros tipos de select (não multiple)
          if (val === undefined || val === null || (typeof val === 'string' && val.trim() === '')) {
            missingFixed.push(name);
          }
          return;
        }

        // 9) campos simples (root, others)
        const val = valuesForm[name];
        if (val === undefined || val === null || (typeof val === 'string' && val.trim() === '')) {
          missingFixed.push(name);
        }
      });

      if (missingFixed.length > 0) {
        console.log('[EditUser validation] missingFixed:', missingFixed);
        setLoading(false);
        toast.warn('Por favor, preencha todos os campos obrigatórios em "Dados Cadastrais".');
        setTabValue(selectedSegment ? 1 : 0);
        setFieldErrors((prev) => ({
          ...prev,
          formFields: missingFixed.reduce((m, key) => {
            m[key] = true;
            return m;
          }, {}),
        }));
        return;
      }
    }

    // seções extras (otherBlocks)
    if (visibleForm.otherBlocks) {
      for (const [blockKey, block] of Object.entries(visibleForm.otherBlocks)) {
        if (!block.active || !block.fields?.length) continue;

        for (const field of block.fields) {
          if (field.fieldType === 'date') {
            const rawDate = valuesForm.otherBlocks?.[blockKey]?.[field.fieldName];

            userInfoObj.otherBlocks ??= {};
            userInfoObj.otherBlocks[blockKey] ??= {};
            userInfoObj.otherBlocks[blockKey][field.fieldName] = formatDateToTimestamp(rawDate);
            continue;
          }

          if (field.fieldType === 'avatar') {
            try {
              const imageData =
                blockKey === 'formfields'
                  ? fileImage?.[field.fieldName]
                  : fileImage?.[blockKey]?.[field.fieldName];

              const isRemoved = imageData === 'removed';

              if (isRemoved || imageData) {
                userInfoObj.otherBlocks ??= {};
                userInfoObj.otherBlocks[blockKey] ??= {};

                if (isRemoved) {
                  userInfoObj.otherBlocks[blockKey][field.fieldName] = '';
                } else {
                  if (imageData.mimeType) {
                    const result = await uploadFiles('userAvatar', {
                      mimeType: imageData.mimeType,
                      fileName: `${userInfo?.aliasName ?? userInfo?.name}_${seuClubePersist.clubeId
                        }_${field.fieldName}`,
                      buffer: imageData.buffer,
                    });

                    userInfoObj.otherBlocks[blockKey][field.fieldName] = result;
                  }
                }
              }
            } catch (err) {
              console.warn('Falha ao enviar avatar de otherBlock:', err);
            }
          }

          if (field.fieldType === 'file') {
            try {
              const fileArray =
                blockKey === 'formfields'
                  ? files?.[field.fieldName]
                  : files?.[blockKey]?.[field.fieldName];

              if (Array.isArray(fileArray) && fileArray.length > 0) {
                const { file, name } = fileArray[0];

                let result;

                if (file.mimeType) {
                  result = await uploadFiles('filesSeuClube', {
                    mimeType: file.mimeType,
                    fileName: `${name}`,
                    buffer: file.buffer,
                  });
                }

                if (result) {
                  userInfoObj.otherBlocks ??= {};
                  userInfoObj.otherBlocks[blockKey] ??= {};
                  userInfoObj.otherBlocks[blockKey][field.fieldName] = {
                    name,
                    url: toStoredUploadPath(result),
                  };
                }
              }
            } catch {
              setLoading(false);
              return toast.error('Erro ao enviar arquivo complementar.');
            }
          }
        }
      }
    }

    // Validação de CEP
    const firstAddressField = form?.formFields?.find((f) => f.type === 'addressList' && f.active);
    const cepEntry = firstAddressField
      ? (userInfoObj.addressList?.find((a) => a.type === firstAddressField.fieldName) ??
        (userInfoObj.addressList?.length === 1 && !userInfoObj.addressList[0].type
          ? userInfoObj.addressList[0]
          : null))
      : userInfoObj.addressList?.[0];
    const cep = cepEntry?.zipCode;
    if (cep && cep.replace(/\D/g, '').length < 8) {
      setLoading(false);
      return toast.warn('O CEP precisa ter 8 dígitos.');
    }

    // Validação de celular
    if (cellPhoneInvalid) {
      setLoading(false);
      return toast.warn('Número de celular inválido.');
    }

    // ——— Validação dinâmica de todas as seções extras antes de salvar na seção fixa ———
    if (visibleForm.otherBlocks) {
      for (const [blockKey, blockConfig] of Object.entries(visibleForm.otherBlocks)) {
        if (!blockConfig.active || !blockConfig.fields?.length) continue;

        const valuesBlock = valuesForm.otherBlocks?.[blockKey] || {};
        const missing = [];

        blockConfig.fields.forEach((field) => {
          if (!field.active || !field.required) return;
          const name = field.fieldName;
          const val = valuesBlock[name];

          // 1) addressList
          if (field.type === 'addressList') {
            const arr = valuesBlock.addressList || [];
            if (!arr.length) {
              missing.push(name);
            } else {
              const zip = arr[0].zipCode || '';
              if (zip.replace(/\D/g, '').length < 8) missing.push(name);
            }
            return;
          }

          // 2) contactList (celular / email)
          if (field.type === 'contactList') {
            const contact = (valuesBlock.contactList || []).find((c) => c.type === name);
            if (field.fieldType === 'cellPhone') {
              if (!contact?.value) {
                missing.push(name);
              } else {
                const mask = contact.mask || phoneMaskArray.find((m) => m.ddd === '+55');
                const digits = contact.value.replace(/\D/g, '');
                const local = digits.replace(mask.ddd.replace('+', ''), '');
                const expected = mask.mask.replace(/\D/g, '').length;
                if (local.length < expected) missing.push(name);
              }
            } else if (field.fieldType === 'email') {
              if (!contact?.value?.trim()) missing.push(name);
            } else {
              if (!contact) missing.push(name);
            }
            return;
          }

          // 3) documentList (CPF / CNPJ / RG)
          if (field.type === 'documentList') {
            const doc = (valuesBlock.documentList || []).find((d) => d.type === name);
            const raw = doc?.value?.replace(/\D/g, '') || '';
            if (!raw) {
              missing.push(name);
            } else if (field.fieldType === 'cpf' && raw.length < 11) {
              missing.push(name);
            } else if (field.fieldType === 'cnpj' && raw.length < 14) {
              missing.push(name);
            } else if (field.fieldType === 'rg' && raw.length < 1) {
              missing.push(name);
            }
            return;
          }

          // 4) optInList
          if (field.type === 'optInList') {
            const arr = valuesBlock.optInList || [];
            const fallbackId = `${field.fieldName}_${blockKey}`;
            const accepted = arr.some(
              (o) =>
                (o.optInId === field.optInId && o.accept) ||
                (o.optInId === field.fieldName && o.accept) ||
                (o.optInId === fallbackId && o.accept)
            );
            if (!accepted) missing.push(name);
            return;
          }

          // 5) checkbox
          if (field.fieldType === 'checkbox') {
            if (!val) missing.push(name);
            return;
          }

          // 6) file
          if (field.fieldType === 'file') {
            const saved = valuesBlock[name];
            const hasSaved = saved && saved.url;
            const newFiles = files?.[blockKey]?.[name] || [];
            if (!hasSaved && !newFiles.length) missing.push(name);
            return;
          }

          // 7) avatar
          if (field.fieldType === 'avatar') {
            const saved = valuesBlock[name];
            const hasSaved = typeof saved === 'string' && saved.trim() !== '';
            const uploaded = fileImage?.[blockKey]?.[name];
            const hasNew = uploaded && uploaded !== 'removed';
            if (!hasSaved && !hasNew) missing.push(name);
            return;
          }

          // 8) select / selectMultiple / maritalStatus (otherBlocks)
          if (
            field.fieldType === 'select' ||
            field.inputType === 'select' ||
            field.inputType === 'selectMultiple' ||
            field.fieldType === 'maritalStatus' ||
            field.inputType === 'maritalStatus'
          ) {
            const valueBlock = valuesBlock[name];

            if (field.inputType === 'selectMultiple') {
              // Mesma lógica do getInvalidFields para consistência
              if (Array.isArray(valueBlock)) {
                if (valueBlock.length === 0) missing.push(name);
              } else if (typeof valueBlock === 'string') {
                const trimmed = valueBlock.trim();
                if (trimmed === '') {
                  missing.push(name);
                } else {
                  try {
                    const parsed = JSON.parse(trimmed);
                    if (Array.isArray(parsed) && parsed.length === 0) {
                      missing.push(name);
                    }
                  } catch {
                    // Não é JSON, verifica se tem conteúdo após split por vírgula
                    const parts = trimmed
                      .split(',')
                      .map((s) => s.trim())
                      .filter(Boolean);
                    if (parts.length === 0) {
                      missing.push(name);
                    }
                  }
                }
              } else if (valueBlock && typeof valueBlock === 'object') {
                // Se for objeto, verifica se tem propriedades
                if (Object.keys(valueBlock).length === 0) {
                  missing.push(name);
                }
              } else {
                // valor undefined, null, etc
                missing.push(name);
              }
              return;
            }

            // Para outros tipos de select (não multiple)
            if (
              valueBlock === undefined ||
              valueBlock === null ||
              (typeof valueBlock === 'string' && valueBlock.trim() === '')
            ) {
              missing.push(name);
            }
            return;
          }

          // 8) date
          if (field.fieldType === 'date') {
            if (!isValidDate(val)) missing.push(name);
            return;
          }

          // 9) number
          if (field.fieldType === 'number' || field.inputType === 'number') {
            if (val === undefined || val === null || val === '') missing.push(name);
            return;
          }

          // 10) text simples
          if (field.type === 'text' || field.fieldType === 'text' || field.inputType === 'text') {
            if (!val || (typeof val === 'string' && val.trim() === '')) {
              missing.push(name);
            }
            return;
          }

          // 11) fallback
          if (val === undefined || val === null || (typeof val === 'string' && val.trim() === '')) {
            missing.push(name);
          }
        });

        if (missing.length > 0) {
          setLoading(false);
          toast.warn(`Por favor, preencha todos os campos obrigatórios em "${blockConfig.label}".`);

          const allBlocks = Object.entries(visibleForm.otherBlocks)
            .filter(
              ([_, block]) =>
                block.active &&
                block.fields?.length > 0 &&
                (block.sectionType === 'predefined' || block.onEdit !== 'not-visible')
            )
            .map(([key]) => key);

          const tabIndex = allBlocks.indexOf(blockKey);
          setTabValue(tabIndex + 1); // redireciona para a aba com erro

          setFieldErrors((prev) => ({
            ...prev,
            otherBlocks: {
              ...prev.otherBlocks,
              [blockKey]: missing.reduce((err, key) => {
                err[key] = true;
                return err;
              }, {}),
            },
          }));
          return;
        }
      }
    }

    // Dados fixos obrigatórios
    userInfoObj.partnerId = seuClubePersist.partnerId;
    userInfoObj.partnerName = seuClubePersist.clubeId;
    userInfoObj.updateDate = new Date().getTime();

    if (userInfoObj.otherBlocks && visibleForm.otherBlocks) {
      Object.entries(visibleForm.otherBlocks).forEach(([blockKey, blockConfig]) => {
        if (!blockConfig.active || !blockConfig.fields?.length) return;

        // Busca os nomes dos campos de telefone celular deste bloco
        const cellPhoneFieldNames = blockConfig.fields
          .filter((field) => field.type === 'contactList' && field.fieldType === 'cellPhone')
          .map((field) => field.fieldName);

        if (!cellPhoneFieldNames.length) return;

        const contactList = userInfoObj.otherBlocks?.[blockKey]?.contactList || [];

        const updatedContactList = contactList.map((contact) => {
          if (cellPhoneFieldNames.includes(contact.type)) {
            return {
              type: contact.type,
              value: `${contact.mask?.ddd || '+55'}${contact.phone || ''}`,
            };
          }
          return contact;
        });

        userInfoObj.otherBlocks[blockKey].contactList = updatedContactList;
      });
    }

    // Limpa campos vazios
    const cleanedPayload = JSON.parse(
      JSON.stringify(userInfoObj, (_, value) => {
        if (value === '' || value === null || value === undefined) return undefined;
        return value;
      })
    );
    if (stopIteration) return;

    editUserPartner(userPersistInfo.uId, seuClubePersist.clubeId, cleanedPayload)
      .then(() => {
        setLoading(false);
        toast.success('Dados atualizados com sucesso.');

        const allEmails = [
          ...(valuesForm.contactList?.filter((c) => c.type?.toLowerCase().includes('email')) || []),
          ...Object.values(valuesForm.otherBlocks || {}).flatMap(
            (section) =>
              section.contactList?.filter((c) => c.type?.toLowerCase().includes('email')) || []
          ),
        ];

        const allEmailValues = allEmails.map((email) => email.value).filter(Boolean);
        setEmailGetUser(allEmailValues);
      })
      .catch((err) => {
        setLoading(false);
        toast.error('Erro ao atualizar dados.');
        console.error('Erro ao editar usuário:', err);
      });
  };

  if (partnerFormLoading) {
    return (
      <Box className="paper-container" role="status" sx={{ mb: 1, textAlign: 'center', py: 4 }}>
        Carregando...
      </Box>
    );
  }

  if (partnerFormError || !form) {
    return (
      <Box className="paper-container" role="alert" sx={{ mb: 1, textAlign: 'center', py: 4 }}>
        <p>{partnerFormError || 'Não foi possível carregar o formulário.'}</p>
        <button type="button" onClick={reloadPartnerForm}>
          Tentar novamente
        </button>
      </Box>
    );
  }

  return (
    <Box className="paper-container" sx={{ mb: 1 }}>
      <GoBack />

      <EditUserPackage
        ambient={cfac22('ambient')}
        project="seuClube"
        title="Editar Meus Dados"
        segment={segment}
        selectedSegment={selectedSegment}
        loadingForm={loadingForm}
        form={visibleForm}
        valuesForm={valuesForm}
        setValuesForm={setValuesForm}
        tabValue={tabValue}
        setTabValue={setTabValue}
        seuClubePersist={seuClubePersist}
        colorText={colorText}
        loading={loading}
        handleInputChange={handleInputChange}
        userInfo={userInfo}
        phoneMaskArray={phoneMaskArray}
        emailGetUser={emailGetUser}
        openModal={openModal}
        setOpenModal={setOpenModal}
        handleValidateEmail={handleValidateEmail}
        handleSubmit={handleSubmit}
        files={files}
        setFiles={setFiles}
        fileImage={fileImage}
        setFileImage={setFileImage}
        fieldErrors={fieldErrors}
        setFieldErrors={setFieldErrors}
      />
    </Box>
  );
}
